import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useStaffView from "../../hooks/useStaffView";
import useStaffPageQuery from "../../hooks/useStaffPageQuery";
import ReadRefreshStatus from "../fragment/ReadRefreshStatus";
import QRCode from "qrcode";
import TeacherClassPermissions from "../fragment/TeacherClassPermissions";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../auth/AuthContext";
import {
    archiveManagedAccount,
    getGuardianEmailStatus,
    getManagedAccounts,
    getManagedNicknameHistory,
    restoreManagedAccount,
    setGuardianEmail,
    updateManagedAccount
} from "../../services/membershipService";
import {
    deleteAcademyInvitation,
    adminResetAcademyStudentPassword,
    deleteAcademyStudentAccount,
    listAcademyInvitations,
    reissueAcademyStudentLoginCard,
    setTeacherClassCodes
} from "../../services/academyStudentService";
import { sendBrandedPasswordResetEmail } from "../../services/authEmailService";
import { sendGuardianReportBatch } from "../../services/guardianEmailService";
import { getPrimaryAccessPlanCode, getPrimaryAccessPlanLabel } from "../../constants/membershipPlans";
import "./css/ManagementDashboard.scss";

const ROLE_LABELS = {
    student: "Student",
    teacher: "Teacher",
    admin: "Admin"
};

const NO_ACTIVE_PLAN_LABELS = {
    academy_student: "英文班學生（目前無有效方案）",
    textbook_customer: "一般會員（目前無有效方案）",
    trial_user: "七天免費試用"
};

const getAccountPlanLabel = account => (
    getPrimaryAccessPlanLabel(account?.membership)
    || NO_ACTIVE_PLAN_LABELS[account?.learner_type]
    || "尚未設定"
);

const getAccountPlanKey = account => (
    getPrimaryAccessPlanCode(account?.membership)
    || `learner:${account?.learner_type || "unknown"}`
);

const ACTIVATION_LABELS = {
    active: "尚未開通",
    claimed: "等待 Email 驗證",
    completed: "已開通",
    expired: "開通碼已過期",
    revoked: "已撤銷"
};

const ACCOUNT_STATUS_LABELS = {
    active: "使用中",
    archived: "已停用"
};

const ACCESS_STATUS_LABELS = {
    enabled: "已啟用",
    disabled: "未啟用"
};

const getAccountActivationStatus = (account, invitationByEmail) => {
    if (account?.role !== "student") return "not_applicable";
    if (account.authentication_method === "academy_username") {
        return account.activated_at ? "completed" : "direct_pending";
    }
    const invitation = invitationByEmail.get(String(account.email || "").toLowerCase());
    return invitation?.status || (account.must_change_password ? "direct_pending" : "legacy");
};

// 面板在列表下方，按下按鈕後自動捲過去（尊重「減少動態效果」設定），並把游標放進第一個欄位。
const scrollPanelIntoView = ref => {
    const element = ref.current;
    if (!element || typeof element.scrollIntoView !== "function") return;
    const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    element.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    element.querySelector("input, textarea")?.focus({ preventScroll: true });
};

function AccountManagement() {
    const { firebaseUser, role, studentProfile } = useAuth();
    const accountQuery = useStaffPageQuery("accounts", async () => {
        const [accountResult, invitations] = await Promise.all([getManagedAccounts(firebaseUser), listAcademyInvitations(firebaseUser)]);
        return { accounts: accountResult?.accounts || [], invitations: invitations || [] };
    });
    const accounts = useMemo(() => accountQuery.data?.accounts || [], [accountQuery.data]);
    const invitations = useMemo(() => accountQuery.data?.invitations || [], [accountQuery.data]);
    const loading = accountQuery.loading;
    const updateAccounts = accountQuery.update;
    const setAccounts = useCallback(updater => updateAccounts(data => ({ ...data, accounts: updater(data.accounts) })), [updateAccounts]);
    const setInvitations = updater => accountQuery.update(data => ({ ...data, invitations: updater(data.invitations) }));
    const [saving, setSaving] = useState(false);
    const [resettingEmail, setResettingEmail] = useState("");
    const [sendingReports, setSendingReports] = useState(false);
    const [reissuingStudentId, setReissuingStudentId] = useState(null);
    const [reissuedLoginCard, setReissuedLoginCard] = useState(null);
    const [reissuedLoginCardQr, setReissuedLoginCardQr] = useState("");
    const [passwordResetTarget, setPasswordResetTarget] = useState(null);
    const [passwordResetForm, setPasswordResetForm] = useState({ username: "", reason: "" });
    const [passwordResetting, setPasswordResetting] = useState(false);
    const [resetPasswordResult, setResetPasswordResult] = useState(null);
    const [guardianChange, setGuardianChange] = useState(null);
    const [guardianChangeForm, setGuardianChangeForm] = useState({ newEmail: "", confirmEmail: "", reason: "" });
    const [guardianChangeBusy, setGuardianChangeBusy] = useState(false);
    const passwordResetPanelRef = useRef(null);
    const guardianChangePanelRef = useRef(null);
    const resetResultPanelRef = useRef(null);
    useEffect(() => { if (passwordResetTarget) scrollPanelIntoView(passwordResetPanelRef); }, [passwordResetTarget]);
    useEffect(() => { if (guardianChange?.account?.id) scrollPanelIntoView(guardianChangePanelRef); }, [guardianChange?.account?.id]);
    useEffect(() => { if (resetPasswordResult) scrollPanelIntoView(resetResultPanelRef); }, [resetPasswordResult]);
    const [changingStatusId, setChangingStatusId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [nicknameHistoryTarget, setNicknameHistoryTarget] = useState(null);
    const [nicknameHistory, setNicknameHistory] = useState([]);
    const [loadingNicknameHistory, setLoadingNicknameHistory] = useState(false);
    const [view, setView] = useStaffView(accountQuery.scope, "accounts", {
        searchText: "", roleFilter: "all", classFilter: "all", planFilter: "all", activationFilter: "all", accountStatusFilter: "active", accessFilter: "all", page: 1, scrollY: 0
    }, Boolean(accountQuery.data));
    const { searchText, roleFilter, classFilter, planFilter, activationFilter, accountStatusFilter, accessFilter } = view;
    const setFilter = (key, value) => { if (!confirmDiscardEdit()) return; setEditingAccount(null); setView(current => ({ ...current, [key]: value, page: 1, scrollY: 0 })); };
    const setSearchText = value => setFilter("searchText", value);
    const setRoleFilter = value => setFilter("roleFilter", value);
    const setClassFilter = value => setFilter("classFilter", value);
    const setPlanFilter = value => setFilter("planFilter", value);
    const setActivationFilter = value => setFilter("activationFilter", value);
    const setAccountStatusFilter = value => setFilter("accountStatusFilter", value);
    const setAccessFilter = value => setFilter("accessFilter", value);
    const accountList = useRef(null);
    const [editingAccount, setEditingAccount] = useState(null);
    const [editForm, setEditForm] = useState({
        name: "",
        role: "student",
        class: "",
        plan: ""
    });

    // 角色剛從學生／管理員改成教師時，先在這裡勾班級，按「儲存修改」後一起存。
    const [pendingTeacherClasses, setPendingTeacherClasses] = useState([]);
    const togglePendingTeacherClass = code => setPendingTeacherClasses(current => current.includes(code)
        ? current.filter(item => item !== code)
        : [...current, code].sort());

    const editingDirty = Boolean(editingAccount && JSON.stringify(editForm) !== JSON.stringify({
        name: editingAccount.name || "", role: editingAccount.role || "student", class: editingAccount.class || "", plan: editingAccount.plan || ""
    }));
    const confirmDiscardEdit = useUnsavedChanges(editingDirty, { enabled: Boolean(accountQuery.scope) });

    const isAdmin = role === "admin";

    useEffect(() => {
        const activationUrl = reissuedLoginCard?.credentials?.activation_url;
        if (!activationUrl) {
            setReissuedLoginCardQr("");
            return undefined;
        }
        let active = true;
        Promise.resolve(QRCode.toDataURL(activationUrl, { width: 220, margin: 1, errorCorrectionLevel: "M" }))
            .then(value => { if (active) setReissuedLoginCardQr(value); })
            .catch(() => { if (active) setReissuedLoginCardQr(""); });
        return () => { active = false; };
    }, [reissuedLoginCard]);

    const invitationByEmail = useMemo(() => {
        const result = new Map();
        invitations.forEach(invitation => {
            const email = String(invitation.invited_email || "").toLowerCase();
            if (email && !result.has(email)) result.set(email, invitation);
        });
        return result;
    }, [invitations]);

    const planOptions = useMemo(() => {
        const options = new Map();
        accounts.forEach(account => {
            if (account.role !== "student") return;
            const key = getAccountPlanKey(account);
            if (!options.has(key)) options.set(key, getAccountPlanLabel(account));
        });
        return Array.from(options, ([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, "zh-Hant"));
    }, [accounts]);

    const filteredAccounts = useMemo(() => {
        const keyword = searchText.trim().toLowerCase();

        return accounts.filter(account => {
            const matchKeyword = !keyword ||
                String(account.name || "").toLowerCase().includes(keyword) ||
                String(account.login_username || "").toLowerCase().includes(keyword) ||
                String(account.email || "").toLowerCase().includes(keyword);
            const matchRole = roleFilter === "all" || account.role === roleFilter;
            const matchClass = classFilter === "all" || account.class === classFilter;
            const matchPlan = planFilter === "all" || (
                account.role === "student" && getAccountPlanKey(account) === planFilter
            );
            const activationStatus = getAccountActivationStatus(account, invitationByEmail);
            const matchActivation = activationFilter === "all"
                || activationStatus === activationFilter;
            const accountStatus = account.account_status || "active";
            const matchAccountStatus = accountStatusFilter === "all"
                || accountStatus === accountStatusFilter;
            const accessStatus = account?.membership?.is_active === true ? "enabled" : "disabled";
            const matchAccess = accessFilter === "all" || (
                account.role === "student" && accessStatus === accessFilter
            );

            return matchKeyword
                && matchRole
                && matchClass
                && matchPlan
                && matchActivation
                && matchAccountStatus
                && matchAccess;
        });
    }, [
        accounts,
        searchText,
        roleFilter,
        classFilter,
        planFilter,
        activationFilter,
        accountStatusFilter,
        accessFilter,
        invitationByEmail
    ]);

    const pageSize = 25;
    const pageCount = Math.max(1, Math.ceil(filteredAccounts.length / pageSize));
    const currentPage = Math.min(pageCount, Math.max(1, Number(view.page) || 1));
    const visibleAccounts = filteredAccounts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const changePage = page => {
        if (!confirmDiscardEdit()) return;
        setEditingAccount(null);
        setView(current => ({ ...current, page: Math.min(pageCount, Math.max(1, page)), scrollY: 0 }));
        accountList.current?.scrollIntoView?.({ block: "start", behavior: "auto" });
    };
    useEffect(() => {
        if (accountQuery.data && view.page !== currentPage) setView(current => ({ ...current, page: currentPage }));
    }, [accountQuery.data, view.page, currentPage, setView]);

    const pendingInvitations = useMemo(() => invitations.filter(invitation => (
        ["active", "claimed", "expired"].includes(invitation.status)
    )), [invitations]);

    const startEdit = account => {
        if ((!isAdmin && account.role !== "student") || !confirmDiscardEdit()) return;

        setEditingAccount(account);
        setPendingTeacherClasses([]);
        setEditForm({
            name: account.name || "",
            role: account.role || "student",
            class: account.class || "",
            plan: account.plan || ""
        });
    };

    const cancelEdit = () => {
        if (saving || !confirmDiscardEdit()) return;
        setEditingAccount(null);
    };

    const sendPasswordReset = async account => {
        if (!firebaseUser || !account?.email) return;
        if (!window.confirm(`要寄送密碼重設信給 ${account.email} 嗎？`)) return;

        setResettingEmail(account.email);
        try {
            await sendBrandedPasswordResetEmail(account.email);
            toast.success("密碼重設信已寄出");
        } catch (error) {
            toast.error(error?.message || "密碼重設信寄送失敗");
        } finally {
            setResettingEmail("");
        }
    };

    const openNicknameHistory = async account => {
        if (!isAdmin || !firebaseUser || account?.role !== "student") return;
        setNicknameHistoryTarget({ ...account, nickname: null });
        setNicknameHistory([]);
        setLoadingNicknameHistory(true);
        try {
            const result = await getManagedNicknameHistory(firebaseUser, account.id);
            setNicknameHistoryTarget(result?.student || account);
            setNicknameHistory(result?.nickname_history || []);
        } catch (error) {
            toast.error(error?.message || "暱稱紀錄讀取失敗");
            setNicknameHistoryTarget(null);
        } finally {
            setLoadingNicknameHistory(false);
        }
    };

    const handleRoleChange = event => {
        const nextRole = event.target.value;
        setEditForm(prev => ({
            ...prev,
            role: nextRole,
            class: nextRole === "student" ? prev.class : "",
            plan: nextRole === "student" ? prev.plan : ""
        }));
    };

    const saveAccount = async event => {
        event.preventDefault();

        if (!editingAccount) return;

        if (!editForm.name.trim()) {
            toast.error("Name 不可空白");
            return;
        }

        if (editForm.role === "student" && !editForm.class) {
            toast.error("英文班學生必須選擇班級");
            return;
        }

        if (!firebaseUser) {
            toast.error("登入狀態已失效，請重新登入");
            return;
        }

        setSaving(true);

        try {
            const result = await updateManagedAccount(firebaseUser, {
                id: editingAccount.id,
                name: editForm.name.trim(),
                role: isAdmin ? editForm.role : "student",
                class: editForm.role === "student" ? editForm.class : null,
                plan: editingAccount.plan || null
            });
            setAccounts(prev => prev.map(account => (
                account.id === result.account.id ? { ...account, ...result.account } : account
            )));

            const becameTeacher = isAdmin && editForm.role === "teacher" && editingAccount.role !== "teacher";
            if (becameTeacher && pendingTeacherClasses.length > 0) {
                try {
                    await setTeacherClassCodes(firebaseUser, editingAccount.id, pendingTeacherClasses);
                } catch (classError) {
                    setEditingAccount(null);
                    toast.error(`角色已改成教師，但班級授權沒有存成功：${classError.message || "請再按「編輯」勾選班級"}`);
                    return;
                }
            }

            setEditingAccount(null);
            toast.success("帳號資料更新成功");
        } catch (error) {
            console.error("帳號更新失敗:", error);
            toast.error(error.message || "帳號更新失敗");
        } finally {
            setSaving(false);
        }
    };

    const changeAccountStatus = async account => {
        if (!isAdmin || !firebaseUser || account?.role !== "student") return;
        const isArchived = account.account_status === "archived";
        const actionLabel = isArchived ? "恢復" : "停用";
        const confirmation = isArchived
            ? `要恢復 ${account.name || account.email} 的登入與使用權嗎？`
            : `要停用 ${account.name || account.email} 嗎？\n\n學習紀錄會保留，之後仍可恢復。`;
        if (!window.confirm(confirmation)) return;

        setChangingStatusId(account.id);
        try {
            const result = isArchived
                ? await restoreManagedAccount(firebaseUser, account.id)
                : await archiveManagedAccount(firebaseUser, account.id, "由帳號管理頁停用");
            setAccounts(prev => prev.map(item => (
                item.id === result.account.id ? { ...item, ...result.account } : item
            )));
            toast.success(isArchived
                ? "帳號已恢復，會重新出現在使用中清單"
                : "帳號已停用並從預設清單隱藏；可切換帳號狀態為已停用後恢復");
        } catch (error) {
            toast.error(error?.message || `帳號${actionLabel}失敗`);
        } finally {
            setChangingStatusId(null);
        }
    };

    const reissueLoginCard = async account => {
        if (!isAdmin || !firebaseUser || !account?.id) return;
        const accountName = account.name || account.login_username || "這位學生";
        if (!window.confirm(`要為 ${accountName} 重新發登入卡嗎？\n\n舊的 QR Code 與所有未使用的復原碼會立刻失效。`)) return;

        setReissuingStudentId(account.id);
        try {
            const result = await reissueAcademyStudentLoginCard(firebaseUser, account.id);
            setReissuedLoginCard(result);
            toast.success("新的登入卡已建立，請立即交給學生或家長保存");
        } catch (error) {
            toast.error(error?.message || "重新發登入卡失敗");
        } finally {
            setReissuingStudentId(null);
        }
    };

    const openPasswordReset = account => {
        if (!isAdmin || !account?.id) return;
        setResetPasswordResult(null);
        setPasswordResetForm({ username: "", reason: "" });
        setPasswordResetTarget(account);
    };

    const closePasswordReset = () => {
        setPasswordResetTarget(null);
        setPasswordResetForm({ username: "", reason: "" });
    };

    const submitPasswordReset = async event => {
        event.preventDefault();
        if (!isAdmin || !firebaseUser || !passwordResetTarget?.id || passwordResetting) return;
        setPasswordResetting(true);
        try {
            const result = await adminResetAcademyStudentPassword(
                firebaseUser,
                passwordResetTarget.id,
                passwordResetForm.username,
                passwordResetForm.reason
            );
            setResetPasswordResult(result);
            closePasswordReset();
            toast.success("新的臨時密碼已產生，請立即抄給學生");
        } catch (error) {
            toast.error(error?.message || "重設密碼失敗");
        } finally {
            setPasswordResetting(false);
        }
    };

    const openGuardianChange = async account => {
        if (!isAdmin || !firebaseUser || !account?.id) return;
        setGuardianChangeForm({ newEmail: "", confirmEmail: "", reason: "" });
        setGuardianChange({ account, status: null });
        try {
            const status = await getGuardianEmailStatus(firebaseUser, account.id);
            setGuardianChange(current => current && current.account.id === account.id ? { ...current, status } : current);
        } catch (error) {
            toast.error(error?.message || "目前無法讀取家長 Email 狀態");
        }
    };

    const closeGuardianChange = () => {
        setGuardianChange(null);
        setGuardianChangeForm({ newEmail: "", confirmEmail: "", reason: "" });
    };

    const submitGuardianChange = async event => {
        event.preventDefault();
        if (!isAdmin || !firebaseUser || !guardianChange?.account?.id || guardianChangeBusy) return;
        const newEmail = guardianChangeForm.newEmail.trim().toLowerCase();
        if (newEmail !== guardianChangeForm.confirmEmail.trim().toLowerCase()) {
            toast.error("兩次輸入的 Email 不一致，請再確認");
            return;
        }
        const accountName = guardianChange.account.name || guardianChange.account.login_username || "這位學生";
        if (!window.confirm(`確定把 ${accountName} 的家長 Email 換成：\n\n${newEmail}\n\n換好之後，週報與所有通知都會寄到這個信箱，這個信箱也能替孩子重設密碼。請確認沒有打錯，而且是家長本人要求更換。`)) return;
        setGuardianChangeBusy(true);
        try {
            await setGuardianEmail(firebaseUser, {
                studentId: guardianChange.account.id,
                newEmail,
                confirmEmail: guardianChangeForm.confirmEmail,
                reason: guardianChangeForm.reason
            });
            toast.success("家長 Email 已更換");
            closeGuardianChange();
        } catch (error) {
            toast.error(error?.message || "更換家長 Email 失敗");
        } finally {
            setGuardianChangeBusy(false);
        }
    };

    const openInvitationDelete = invitation => {
        if (!isAdmin || invitation.claimed_by_student_id) return;
        const email = String(invitation.invited_email || "").toLowerCase();
        setDeleteTarget({
            type: "invitation",
            id: invitation.id,
            identifier: email,
            identifierLabel: "Email",
            email,
            name: invitation.chinese_name || invitation.invited_email || "待開通學生"
        });
        setDeleteConfirmation("");
    };

    const openAccountDelete = account => {
        const usesUsername = account?.authentication_method === "academy_username";
        const identifier = usesUsername ? account?.login_username : account?.email;
        if (!isAdmin || account?.role !== "student" || !identifier) return;
        setDeleteTarget({
            type: "account",
            id: account.id,
            identifier: String(identifier).toLowerCase(),
            identifierLabel: usesUsername ? "登入名稱" : "Email",
            email: String(account.email || "").toLowerCase(),
            name: account.name || identifier || "學生帳號"
        });
        setDeleteConfirmation("");
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setDeleteTarget(null);
        setDeleteConfirmation("");
    };

    const confirmPermanentDelete = async event => {
        event.preventDefault();
        if (!firebaseUser || !deleteTarget) return;
        const normalizedConfirmation = deleteConfirmation.trim().toLowerCase();
        if (normalizedConfirmation !== deleteTarget.identifier) {
            toast.error(`請輸入完整${deleteTarget.identifierLabel}確認永久刪除`);
            return;
        }

        setDeleting(true);
        try {
            if (deleteTarget.type === "account") {
                await deleteAcademyStudentAccount(
                    firebaseUser,
                    deleteTarget.id,
                    normalizedConfirmation
                );
                setAccounts(prev => prev.filter(account => account.id !== deleteTarget.id));
                setInvitations(prev => prev.filter(invitation => (
                    String(invitation.invited_email || "").toLowerCase() !== deleteTarget.email
                )));
                toast.success("學生帳號與測試紀錄已永久刪除");
            } else {
                await deleteAcademyInvitation(
                    firebaseUser,
                    deleteTarget.id,
                    normalizedConfirmation
                );
                setInvitations(prev => prev.filter(invitation => invitation.id !== deleteTarget.id));
                toast.success("待開通邀請已刪除");
            }
            setDeleteTarget(null);
            setDeleteConfirmation("");
        } catch (error) {
            toast.error(error?.message || "永久刪除失敗");
        } finally {
            setDeleting(false);
        }
    };

    const editingOwnAdminAccount = Boolean(
        isAdmin &&
        editingAccount &&
        editingAccount.firebase_uid === studentProfile?.firebase_uid
    );

    const advancedFilterCount = [isAdmin ? roleFilter : "all", planFilter, activationFilter, accessFilter].filter(value => value !== "all").length;

    const resetFilters = () => {
        if (!confirmDiscardEdit()) return;
        setEditingAccount(null);
        setView(current => ({ ...current, searchText: "", roleFilter: "all", classFilter: "all", planFilter: "all", activationFilter: "all", accountStatusFilter: "active", accessFilter: "all", page: 1, scrollY: 0 }));
    };

    // 一鍵寄出本週家長週報（後端只允許管理員；寄出後無法收回，所以先確認一次）。
    const sendWeeklyReports = async () => {
        if (sendingReports) return;
        if (!window.confirm("確定現在寄送本週家長週報給所有已啟用的家長嗎？寄出後無法收回。")) return;
        setSendingReports(true);
        try {
            const result = await sendGuardianReportBatch(firebaseUser);
            toast.success(`週報寄送完成：成功 ${result?.totals?.sent || 0}、略過 ${result?.totals?.skipped || 0}、失敗 ${result?.totals?.failed || 0}`);
        } catch (error) {
            toast.error(error?.message || "週報批次寄送失敗");
        } finally {
            setSendingReports(false);
        }
    };

    return (
        <div className="management-page">
            <section className="management-hero">
                <div>
                    <span className="management-eyebrow">Account Management</span>
                    <h1>帳號管理</h1>
                    <p>
                        {isAdmin
                            ? "查看並安全編輯 Alan English 的學生、教師與管理員帳號。"
                            : "查看學生帳號，並修改學生姓名、班級與方案。"}
                    </p>
                </div>

                <div className="management-hero-actions">
                    <Link to="/teacher/students" className="management-primary-link">
                        快速建立學生
                    </Link>
                    {isAdmin && <Link to="/admin/accounts/create-staff" className="management-primary-link">
                        新增教師／管理員
                    </Link>}
                    {isAdmin && <button type="button" className="management-secondary-button" onClick={sendWeeklyReports} disabled={sendingReports}>
                        {sendingReports ? "寄送中…" : "寄送本週家長週報"}
                    </button>}
                </div>
            </section>

            <section className="management-panel">
                <ReadRefreshStatus query={accountQuery} label="帳號清單" />
                <div className="management-toolbar"><label className="management-filter management-search-filter">
                        <span>搜尋</span>
                        <input
                            type="search"
                            placeholder="姓名或 Email"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                        />
                    </label>
<label className="management-filter">
                        <span>班級</span>
                        <select value={classFilter} onChange={e => setClassFilter(e.target.value)}>
                            <option value="all">全部班級</option>
                            <option value="E1">E1 班</option>
                            <option value="E3">E3 班</option>
                            <option value="E5">E5 班</option>
                            <option value="E7">E7 班</option>
                        </select>
                    </label>
<label className="management-filter">
                        <span>帳號狀態</span>
                        <select value={accountStatusFilter} onChange={e => setAccountStatusFilter(e.target.value)}>
                            <option value="active">使用中（預設）</option>
                            <option value="archived">已停用</option>
                            <option value="all">全部帳號狀態</option>
                        </select>
                    </label><button type="button" className="management-filter-reset" onClick={resetFilters}>
                        清除篩選
                    </button></div><details className="management-advanced-filters"><summary>進階篩選{advancedFilterCount > 0 ? "（已套用 " + advancedFilterCount + " 項）" : ""}</summary><div className="management-toolbar">{isAdmin && (
                        <label className="management-filter">
                            <span>角色</span>
                            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                                <option value="all">全部角色</option>
                                <option value="student">學生</option>
                                <option value="teacher">教師</option>
                                <option value="admin">管理員</option>
                            </select>
                        </label>
                    )}
<label className="management-filter">
                        <span>方案</span>
                        <select value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
                            <option value="all">全部方案</option>
                            {planOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>
<label className="management-filter">
                        <span>開通狀態</span>
                        <select value={activationFilter} onChange={e => setActivationFilter(e.target.value)}>
                            <option value="all">全部開通狀態</option>
                            <option value="direct_pending">待首次改密碼</option>
                            <option value="active">尚未開通</option>
                            <option value="claimed">等待 Email 驗證</option>
                            <option value="completed">已開通（邀請）</option>
                            <option value="legacy">已開通</option>
                            <option value="expired">開通碼已過期</option>
                            <option value="revoked">已撤銷</option>
                            <option value="not_applicable">不適用</option>
                        </select>
                    </label>
<label className="management-filter">
                        <span>是否啟用</span>
                        <select value={accessFilter} onChange={e => setAccessFilter(e.target.value)}>
                            <option value="all">全部啟用狀態</option>
                            <option value="enabled">已啟用</option>
                            <option value="disabled">未啟用</option>
                        </select>
                    </label></div></details>

                <p className="management-filter-hint">
                    已停用帳號預設隱藏；將「帳號狀態」切換為「已停用」即可查看並恢復。
                    「是否啟用」代表目前是否具有有效學習權限。
                </p>

                {loading ? (
                    <div className="management-state">正在讀取帳號資料...</div>
                ) : !accountQuery.data ? (
                    <div className="management-state">重新讀取成功後，這裡會顯示帳號清單。</div>
                ) : (
                    <>
                        <div className="management-count" ref={accountList}>
                            <span>顯示 {filteredAccounts.length} 筆／全部 {accounts.length} 筆帳號</span>
                            {filteredAccounts.length > pageSize && <span>目前第 {(currentPage - 1) * pageSize + 1}～{Math.min(currentPage * pageSize, filteredAccounts.length)} 筆</span>}
                        </div>

                        <div className="management-table-wrap management-account-table-wrap">
                            <table className="management-table management-account-table" aria-label="帳號清單">
                                <thead>
                                    <tr>
                                        <th>姓名</th>
                                        <th>帳號／Email</th>
                                        <th>角色</th>
                                        <th>班級</th>
                                        <th>方案</th>
                                        <th>開通狀態</th>
                                        <th>帳號狀態</th>
                                        <th>是否啟用</th>
                                        <th>操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleAccounts.length > 0 ? visibleAccounts.map(account => (
                                        <React.Fragment key={account.id}>
                                            <tr className={`management-account-row ${account.account_status === "archived" ? "is-archived" : ""}`}>
                                                <td data-label="姓名">
                                                    <span className="management-account-name">{account.name || "-"}</span>
                                                    {account.account_status === "archived" && (
                                                        <span className="management-account-archived-note">已停用帳號</span>
                                                    )}
                                                </td>
                                                <td data-label="帳號／Email">{account.authentication_method === "academy_username" ? account.login_username || "-" : account.email || "-"}</td>
                                                <td data-label="角色">
                                                    <span className={`role-badge role-${account.role || "student"}`}>
                                                        {ROLE_LABELS[account.role] || account.role || "Student"}
                                                    </span>
                                                </td>
                                                <td data-label="班級">{account.role === "student" ? account.class || "-" : "-"}</td>
                                                <td data-label="方案">{account.role === "student" ? getAccountPlanLabel(account) : "-"}</td>
                                                <td data-label="開通狀態">
                                                    {account.role === "student" ? (() => {
                                                        const status = getAccountActivationStatus(account, invitationByEmail);
                                                        return (
                                                            <span className={`activation-badge activation-${status}`}>
                                                                {status === "direct_pending"
                                                                    ? "待掃碼啟用"
                                                                    : ACTIVATION_LABELS[status] || "已開通"}
                                                            </span>
                                                        );
                                                    })() : "-"}
                                                </td>
                                                <td data-label="帳號狀態">
                                                    <span className={`account-status-badge account-status-${account.account_status || "active"}`}>
                                                        {ACCOUNT_STATUS_LABELS[account.account_status || "active"]}
                                                    </span>
                                                </td>
                                                <td data-label="是否啟用">
                                                    {account.role === "student" ? (() => {
                                                        const status = account?.membership?.is_active === true
                                                            ? "enabled"
                                                            : "disabled";
                                                        return (
                                                            <span className={`access-status-badge access-status-${status}`}>
                                                                {ACCESS_STATUS_LABELS[status]}
                                                            </span>
                                                        );
                                                    })() : "-"}
                                                </td>
                                                <td data-label="操作">
                                                    <div className="management-row-actions">
                                                        {account.account_status !== "archived" && (
                                                            <button
                                                                type="button"
                                                                className="management-edit-button"
                                                                onClick={() => startEdit(account)}
                                                            >
                                                                編輯
                                                            </button>
                                                        )}
                                                        {isAdmin && account.role === "student" && (
                                                            <button
                                                                type="button"
                                                                className="management-edit-button"
                                                                onClick={() => openNicknameHistory(account)}
                                                            >
                                                                暱稱紀錄
                                                            </button>
                                                        )}
                                                        {isAdmin && account.role === "student" && account.account_status !== "archived" && (
                                                            <button type="button" className="management-edit-button" onClick={() => openGuardianChange(account)}>更換家長 Email</button>
                                                        )}
                                                        {isAdmin && account.email && account.authentication_method !== "academy_username" && account.account_status !== "archived" && (
                                                            <button
                                                                type="button"
                                                                className="management-reset-button"
                                                                onClick={() => sendPasswordReset(account)}
                                                                disabled={resettingEmail === account.email}
                                                            >
                                                                {resettingEmail === account.email ? "寄送中…" : "寄送密碼重設信"}
                                                            </button>
                                                        )}
                                                        {isAdmin && account.authentication_method === "academy_username" && !account.activated_at && account.account_status !== "archived" && (
                                                            <button
                                                                type="button"
                                                                className="management-reset-button"
                                                                onClick={() => reissueLoginCard(account)}
                                                                disabled={reissuingStudentId === account.id}
                                                            >
                                                                {reissuingStudentId === account.id ? "發卡中…" : "重新發登入卡"}
                                                            </button>
                                                        )}
                                                        {isAdmin && account.authentication_method === "academy_username" && account.activated_at && account.account_status !== "archived" && (
                                                            <button type="button" className="management-reset-button" onClick={() => openPasswordReset(account)}>重設密碼</button>
                                                        )}
                                                        {isAdmin && account.role === "student" && account.account_status === "archived" && (
                                                            <button
                                                                type="button"
                                                                className="management-restore-button"
                                                                onClick={() => changeAccountStatus(account)}
                                                                disabled={changingStatusId === account.id}
                                                            >
                                                                {changingStatusId === account.id ? "處理中…" : "恢復"}
                                                            </button>
                                                        )}
                                                        {/* 停用、永久刪除放在最後並和常用按鈕隔開，避免誤按。 */}
                                                        {isAdmin && account.role === "student" && (account.account_status !== "archived" || account.email || account.login_username) && (
                                                            <div className="management-row-danger" role="group" aria-label={`${account.name || "此帳號"}的危險操作`}>
                                                                {account.account_status !== "archived" && (
                                                                    <button
                                                                        type="button"
                                                                        className="management-archive-button"
                                                                        onClick={() => changeAccountStatus(account)}
                                                                        disabled={changingStatusId === account.id}
                                                                    >
                                                                        {changingStatusId === account.id ? "處理中…" : "停用"}
                                                                    </button>
                                                                )}
                                                                {(account.email || account.login_username) && (
                                                                    <button
                                                                        type="button"
                                                                        className="management-delete-button"
                                                                        onClick={() => openAccountDelete(account)}
                                                                    >
                                                                        永久刪除
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>

                                            {editingAccount?.id === account.id && (
                                                <tr className="management-edit-row">
                                                    <td colSpan="9">
                                                        <form className="management-edit-form" onSubmit={saveAccount}>
                                                            <div className="management-edit-grid">
                                                                <label>
                                                                    <span>姓名</span>
                                                                    <input
                                                                        type="text"
                                                                        value={editForm.name}
                                                                        onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                                                        disabled={saving}
                                                                    />
                                                                </label>

                                                                <label>
                                                                    <span>角色</span>
                                                                    <select
                                                                        value={editForm.role}
                                                                        onChange={handleRoleChange}
                                                                        disabled={!isAdmin || saving || editingOwnAdminAccount}
                                                                    >
                                                                        <option value="student">學生</option>
                                                                        <option value="teacher">教師</option>
                                                                        <option value="admin">管理員</option>
                                                                    </select>
                                                                </label>

                                                                {editForm.role === "student" && (
                                                                    <>
                                                                        <label>
                                                                            <span>班級</span>
                                                                            <select
                                                                                value={editForm.class}
                                                                                onChange={e => setEditForm(prev => ({ ...prev, class: e.target.value }))}
                                                                                disabled={saving}
                                                                            >
                                                                                <option value="">選擇 Class</option>
                                                                                <option value="E1">E1</option>
                                                                                <option value="E3">E3</option>
                                                                                <option value="E5">E5</option>
                                                                                <option value="E7">E7</option>
                                                                            </select>
                                                                        </label>

                                                                        <label>
                                                                            <span>Plan（自動判定）</span>
                                                                            <input
                                                                                type="text"
                                                                                value={getAccountPlanLabel(editingAccount)}
                                                                                disabled
                                                                                readOnly
                                                                            />
                                                                        </label>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {isAdmin && editForm.role === "teacher" && (editingAccount.role === "teacher"
                                                                ? <TeacherClassPermissions teacherId={editingAccount.id} disabled={saving} />
                                                                : <fieldset className="staff-create-classes teacher-class-permissions">
                                                                    <legend>可以管理的班級（點名、發作業、看報表）</legend>
                                                                    <div>
                                                                        {["E1", "E3", "E5", "E7"].map(code => <button type="button" key={code} className="attendance-status present" aria-pressed={pendingTeacherClasses.includes(code)} disabled={saving} onClick={() => togglePendingTeacherClass(code)}>{code}</button>)}
                                                                    </div>
                                                                    <p className="management-edit-note">按「儲存修改」時，角色和班級會一起存。</p>
                                                                </fieldset>)}

                                                            <div className="management-edit-actions">
                                                                <button type="button" className="management-cancel-button" onClick={cancelEdit} disabled={saving}>
                                                                    取消
                                                                </button>
                                                                <button type="submit" className="management-save-button" disabled={saving}>
                                                                    {saving ? "儲存中..." : "儲存修改"}
                                                                </button>
                                                            </div>
                                                        </form>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )) : (
                                        <tr>
                                            <td colSpan="9" className="management-empty">沒有符合條件的帳號</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {pageCount > 1 && <nav className="management-pagination" aria-label="帳號清單分頁">
                            <button type="button" onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1}>上一頁</button>
                            <span role="status">第 {currentPage}／{pageCount} 頁</span>
                            <button type="button" onClick={() => changePage(currentPage + 1)} disabled={currentPage === pageCount}>下一頁</button>
                        </nav>}
                    </>
                )}
            </section>

            {guardianChange && (
                <section className="management-panel" ref={guardianChangePanelRef} aria-labelledby="guardian-change-title">
                    <div className="management-pending-heading">
                        <div>
                            <span className="management-eyebrow">Guardian email</span>
                            <h2 id="guardian-change-title">更換 {guardianChange.account.name || guardianChange.account.login_username} 的家長 Email</h2>
                            <p>
                                目前的家長 Email：<strong>{guardianChange.status ? (guardianChange.status.current_email_masked || "尚未設定") : "讀取中…"}</strong>
                                {guardianChange.status?.current_email_masked ? `（${guardianChange.status.current_email_verified ? "已驗證" : "尚未驗證"}）` : ""}。
                                這裡是<strong>直接更換，不需要驗證碼</strong>（例如離婚的家庭要把信箱改掛另一位家長）。換好後，週報與所有通知都寄到新信箱，新信箱也能替孩子重設密碼，所以<strong>請先確認是家長本人要求更換</strong>（聯絡簿留言或電話），並且不要打錯。
                            </p>
                        </div>
                    </div>
                    <form className="management-form" onSubmit={submitGuardianChange}>
                        <label>
                            <span>新的家長 Email</span>
                            <input type="email" value={guardianChangeForm.newEmail} onChange={event => setGuardianChangeForm(current => ({ ...current, newEmail: event.target.value }))} autoComplete="off" required />
                        </label>
                        <label>
                            <span>再輸入一次新的家長 Email</span>
                            <input type="email" value={guardianChangeForm.confirmEmail} onChange={event => setGuardianChangeForm(current => ({ ...current, confirmEmail: event.target.value }))} autoComplete="off" required />
                        </label>
                        <label>
                            <span>更換原因（會留下紀錄）</span>
                            <textarea value={guardianChangeForm.reason} onChange={event => setGuardianChangeForm(current => ({ ...current, reason: event.target.value }))} maxLength={300} rows={2} required />
                        </label>
                        <div className="management-form-actions">
                            <button type="button" className="management-edit-button" onClick={closeGuardianChange} disabled={guardianChangeBusy}>取消</button>
                            <button type="submit" className="management-reset-button" disabled={guardianChangeBusy || !guardianChangeForm.newEmail.trim() || !guardianChangeForm.confirmEmail.trim() || guardianChangeForm.reason.trim().length < 2}>{guardianChangeBusy ? "更換中…" : "更換家長 Email"}</button>
                        </div>
                    </form>
                </section>
            )}

            {passwordResetTarget && (
                <section className="management-panel" ref={passwordResetPanelRef} aria-labelledby="password-reset-title">
                    <div className="management-pending-heading">
                        <div>
                            <span className="management-eyebrow">Reset password</span>
                            <h2 id="password-reset-title">重設 {passwordResetTarget.name || passwordResetTarget.login_username} 的密碼</h2>
                            <p>系統會產生一組新的臨時密碼，<strong>只顯示一次</strong>；舊密碼和舊的登入狀態會立刻失效，學生下次登入必須自己再設定新密碼。你看不到學生自己設定的密碼。</p>
                        </div>
                    </div>
                    <form className="management-form" onSubmit={submitPasswordReset}>
                        <label>
                            <span>輸入這位學生的登入帳號（{passwordResetTarget.login_username}）來確認</span>
                            <input value={passwordResetForm.username} onChange={event => setPasswordResetForm(current => ({ ...current, username: event.target.value }))} autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="off" required />
                        </label>
                        <label>
                            <span>重設原因（會留下紀錄）</span>
                            <textarea value={passwordResetForm.reason} onChange={event => setPasswordResetForm(current => ({ ...current, reason: event.target.value }))} maxLength={300} rows={2} required />
                        </label>
                        <div className="management-form-actions">
                            <button type="button" className="management-edit-button" onClick={closePasswordReset} disabled={passwordResetting}>取消</button>
                            <button type="submit" className="management-reset-button" disabled={passwordResetting || passwordResetForm.username.trim().length < 3 || passwordResetForm.reason.trim().length < 2}>{passwordResetting ? "重設中…" : "產生新的臨時密碼"}</button>
                        </div>
                    </form>
                </section>
            )}

            {resetPasswordResult?.credentials && (
                <section className="management-panel management-login-card-panel" ref={resetResultPanelRef} aria-live="polite">
                    <div className="management-pending-heading">
                        <div>
                            <span className="management-eyebrow">Temporary password</span>
                            <h2>新的臨時密碼</h2>
                            <p>此密碼只在目前畫面顯示一次，關閉後就看不到。請立即抄給學生；學生用登入帳號加這組臨時密碼登入後，要自己設定新密碼。</p>
                        </div>
                    </div>
                    <div className="management-login-card">
                        <div>
                            <span>學生</span>
                            <strong>{resetPasswordResult.account?.name || "-"}</strong>
                            <span>登入帳號</span>
                            <strong>{resetPasswordResult.credentials.username || "-"}</strong>
                        </div>
                        <div className="management-login-card-recovery">
                            <span>臨時密碼</span>
                            <strong>{resetPasswordResult.credentials.temporary_password}</strong>
                            <small>舊密碼已失效。登入卡上的復原碼不受影響。</small>
                        </div>
                    </div>
                    <div className="management-form-actions">
                        <button type="button" className="management-edit-button" onClick={() => setResetPasswordResult(null)}>已抄下，關閉</button>
                    </div>
                </section>
            )}

            {reissuedLoginCard?.credentials && (
                <section className="management-panel management-login-card-panel" aria-live="polite">
                    <div className="management-pending-heading">
                        <div>
                            <span className="management-eyebrow">New login card</span>
                            <h2>新的學生登入卡</h2>
                            <p>此資料只在目前畫面顯示；請立即列印或交給學生／家長保存。</p>
                        </div>
                        <button type="button" className="management-edit-button" onClick={() => window.print()}>列印登入卡</button>
                    </div>
                    <div className="management-login-card">
                        <div>
                            <span>學生</span>
                            <strong>{reissuedLoginCard.account?.name || "-"}</strong>
                            <span>登入帳號</span>
                            <strong>{reissuedLoginCard.credentials.username || "-"}</strong>
                            <small>首次使用請掃 QR Code，並設定至少 6 個字元的密碼。</small>
                        </div>
                        <div className="management-login-card-qr">
                            {reissuedLoginCardQr
                                ? <img src={reissuedLoginCardQr} alt="學生帳號啟用 QR Code" width="180" height="180" />
                                : <span>QR Code 產生中…</span>}
                        </div>
                        <div className="management-login-card-recovery">
                            <span>一次性復原碼</span>
                            <strong>{reissuedLoginCard.credentials.recovery_codes?.join("　")}</strong>
                            <small>每組只能使用一次；新卡建立後，舊卡已立即失效。</small>
                        </div>
                    </div>
                </section>
            )}

            <section className="management-panel">
                <div className="management-pending-heading">
                    <div>
                        <span className="management-eyebrow">Account Activation</span>
                        <h2>待開通學生</h2>
                        <p>學生完成設定密碼與 Email 驗證後，狀態會自動更新為已開通。</p>
                    </div>
                    <strong>{pendingInvitations.length} 位</strong>
                </div>

                <div className="management-table-wrap">
                    <table className="management-table">
                        <thead>
                            <tr>
                                <th>學生</th>
                                <th>登入 Email</th>
                                <th>班級</th>
                                <th>開通狀態</th>
                                <th>開通期限</th>
                                <th>操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingInvitations.length > 0 ? pendingInvitations.map(invitation => (
                                <tr key={invitation.id}>
                                    <td>{invitation.chinese_name || "-"}</td>
                                    <td>{invitation.invited_email || "-"}</td>
                                    <td>{invitation.class_code || "-"}</td>
                                    <td>
                                        <span className={`activation-badge activation-${invitation.status}`}>
                                            {ACTIVATION_LABELS[invitation.status] || invitation.status}
                                        </span>
                                    </td>
                                    <td>{invitation.expires_at ? new Date(invitation.expires_at).toLocaleDateString("zh-TW") : "-"}</td>
                                    <td>
                                        {isAdmin && !invitation.claimed_by_student_id ? (
                                            <button
                                                type="button"
                                                className="management-delete-button"
                                                onClick={() => openInvitationDelete(invitation)}
                                            >
                                                刪除邀請
                                            </button>
                                        ) : invitation.claimed_by_student_id ? "請於上方停用帳號" : "-"}
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="6" className="management-empty">目前沒有待開通學生</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {deleteTarget && (
                <div className="management-delete-backdrop" role="presentation">
                    <section
                        className="management-delete-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="management-delete-title"
                    >
                        <span className="management-eyebrow">
                            {deleteTarget.type === "account" ? "Account cleanup" : "Invitation cleanup"}
                        </span>
                        <h2 id="management-delete-title">
                            {deleteTarget.type === "account" ? "永久刪除學生帳號" : "刪除待開通邀請"}
                        </h2>
                        <p>
                            {deleteTarget.type === "account"
                                ? "這會永久刪除 Firebase 登入帳號，以及相關的測試、學習、作業、AI 與獎勵紀錄，無法復原。Stripe 測試模式付款可一併清理；正式、無法確認模式的付款、教材購買或啟用碼兌換紀錄會拒絕刪除。"
                                : "這會永久移除尚未被領取、且尚未建立學生帳號的邀請。"}
                        </p>
                        <div className="management-delete-target">
                            <strong>{deleteTarget.name}</strong>
                            <span>{deleteTarget.identifier}</span>
                        </div>
                        <form onSubmit={confirmPermanentDelete}>
                            <label>
                                <span>{`輸入完整 ${deleteTarget.identifierLabel} 確認`}</span>
                                <input
                                    type={deleteTarget.identifierLabel === "Email" ? "email" : "text"}
                                    value={deleteConfirmation}
                                    onChange={event => setDeleteConfirmation(event.target.value)}
                                    placeholder={deleteTarget.identifier}
                                    autoFocus
                                    disabled={deleting}
                                />
                            </label>
                            <div className="management-delete-dialog__actions">
                                <button type="button" onClick={closeDeleteDialog} disabled={deleting}>取消</button>
                                <button
                                    type="submit"
                                    disabled={deleting || deleteConfirmation.trim().toLowerCase() !== deleteTarget.identifier}
                                >
                                    {deleting
                                        ? "刪除中…"
                                        : deleteTarget.type === "account" ? "確認永久刪除帳號" : "確認刪除邀請"}
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            )}

            {nicknameHistoryTarget && (
                <div className="management-delete-backdrop" role="presentation">
                    <section
                        className="management-delete-dialog management-nickname-history-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="management-nickname-history-title"
                    >
                        <span className="management-eyebrow">Nickname history</span>
                        <h2 id="management-nickname-history-title">暱稱更改紀錄</h2>
                        <div className="management-nickname-history-student">
                            <strong>{nicknameHistoryTarget.name || "學生"}</strong>
                            <span>{nicknameHistoryTarget.class ? `${nicknameHistoryTarget.class} 班` : "未分班"}</span>
                            <span>目前暱稱：{nicknameHistoryTarget.nickname || "尚未設定"}</span>
                        </div>
                        {loadingNicknameHistory ? (
                            <p className="management-nickname-history-empty">正在讀取紀錄…</p>
                        ) : nicknameHistory.length ? (
                            <ol className="management-nickname-history-list">
                                {nicknameHistory.map(item => (
                                    <li key={item.id}>
                                        <div>
                                            <strong>{item.previous_nickname || "首次設定"}</strong>
                                            <span aria-hidden="true">→</span>
                                            <strong>{item.new_nickname}</strong>
                                        </div>
                                        <time dateTime={item.changed_at}>{new Date(item.changed_at).toLocaleString("zh-TW")}</time>
                                    </li>
                                ))}
                            </ol>
                        ) : <p className="management-nickname-history-empty">尚無暱稱更改紀錄。</p>}
                        <div className="management-delete-dialog__actions">
                            <button type="button" onClick={() => setNicknameHistoryTarget(null)}>關閉</button>
                        </div>
                    </section>
                </div>
            )}

        </div>
    );
}

export default AccountManagement;
